import * as I from 'immutable';
import React from 'react';

import OrderImageryModal from 'app/components/OrderImageryModal';
import TaskImageryModal from 'app/components/TaskImageryModal/TaskImageryModal';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {getAreaUnit} from 'app/modules/Remote/Organization';
import CustomizeTagsModal from 'app/pages/MonitorProjectView/CustomizeTagsDialog';
import {LoggedInUserActions, useUserInfo} from 'app/providers/AuthProvider';
import {useOrderableScenesLoader} from 'app/providers/OrderableScenesProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {CachedApiGetterOptions, StatusMaybe} from 'app/utils/hookUtils';
import {findSceneByOrderType} from 'app/utils/imageryUtils';
import {TAG_KIND} from 'app/utils/tagUtils';

interface Props {
  orderImageryInfo: {
    feature: I.ImmutableOf<ApiFeature>;
    orderableScene: I.ImmutableOf<ApiOrderableScene>;
  } | null;
  setOrderImageryInfo: (
    info: {
      feature: I.ImmutableOf<ApiFeature>;
      orderableScene: I.ImmutableOf<ApiOrderableScene>;
    } | null
  ) => void;
  isCustomizeTagsModalOpen: boolean;
  setIsCustomizeTagsModalOpen: (open: boolean) => void;
  isTaskImageryModalOpen: boolean;
  setIsTaskImageryModalOpen: (open: boolean) => void;
  isTaskImageryModalEnabled: boolean;
  selectedFeatures: ApiFeature[];
  projectId: string;
  featureCollectionId: number;
  getCurrentImageryContract: (projectId: string, opts?: CachedApiGetterOptions) => StatusMaybe<any>;
  loggedInUserActions: LoggedInUserActions;
}

export const PropertyOverviewModals: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  orderImageryInfo,
  setOrderImageryInfo,
  isCustomizeTagsModalOpen,
  setIsCustomizeTagsModalOpen,
  isTaskImageryModalOpen,
  setIsTaskImageryModalOpen,
  isTaskImageryModalEnabled,
  selectedFeatures,
  projectId,
  featureCollectionId,
  getCurrentImageryContract,
  loggedInUserActions,
}) => {
  const [organization, profile] = useUserInfo();
  const areaUnit = getAreaUnit(organization);

  const [projects] = useProjects();
  const projectName = projects?.find((p) => p!.get('id') === projectId)?.get('name');

  const [orderableScenesLoader] = useOrderableScenesLoader(featureCollectionId, projectId);
  const orderableScenesStatusMaybe = orderImageryInfo
    ? orderableScenesLoader(orderImageryInfo.feature.get('id'))
    : undefined;
  const orderableScenes =
    orderableScenesStatusMaybe?.status === 'some' ? orderableScenesStatusMaybe.value : undefined;

  return (
    <>
      {orderImageryInfo && (
        <OrderImageryModal
          feature={orderImageryInfo.feature}
          orderableScene={orderImageryInfo.orderableScene}
          subsetFeature={null}
          organization={organization}
          role={profile.get('role')}
          getCurrentImageryContract={(opts) => getCurrentImageryContract(projectId, opts)}
          selectedProjectId={projectId}
          projectName={projectName}
          onClose={() => setOrderImageryInfo(null)}
          onOrderTypeChange={(orderType) => {
            if (!orderableScenes) return;

            const scene = findSceneByOrderType(
              orderableScenes,
              orderImageryInfo.orderableScene.get('sensingTime'),
              orderImageryInfo.orderableScene.get('sourceId'),
              orderType
            );

            if (scene) {
              setOrderImageryInfo({...orderImageryInfo, orderableScene: scene});
            }
          }}
          orderableScenes={orderableScenes}
        />
      )}
      {isCustomizeTagsModalOpen && (
        <CustomizeTagsModal
          organization={organization}
          setTags={loggedInUserActions.setOrganizationTags}
          onClose={() => setIsCustomizeTagsModalOpen(false)}
          kind={TAG_KIND.FEATURE}
        />
      )}
      {isTaskImageryModalEnabled && (
        <TaskImageryModal
          features={selectedFeatures}
          areaUnit={areaUnit}
          isOpen={isTaskImageryModalOpen}
          onClose={() => setIsTaskImageryModalOpen(false)}
        />
      )}
    </>
  );
};
